// External
import axios from "axios";
import moment from "moment";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { useQuery } from 'react-query';
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { logout, reset } from "../../../features/auth/authSlice";
import { setHeaderText } from "../../../features/headerText/headerTextSlice";
import { reset as invoiceReset } from "../../../features/invoice/invoiceSlice.js";
import { reset as headerReset } from "../../../features/headerText/headerTextSlice.js";
import { reset as appSettingsReset } from "../../../features/appSettings/appSettingsSlice.js";
import ManageTimeOff from "./ManageTimeOff.jsx";
import TimeOffActions from "./TimeOffActions.jsx";

const fetchTimeOffData = async ({ queryKey }) => {
  const [, id, token] = queryKey;
  if (!token) {
    throw new Error("User not authenticated");
  }
  const response = await axios.get(`/api/time-off-request/view/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

const Single = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const componentRef = useRef();
  const [timeOffData, setTimeOffData] = useState(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editMountNode, setEditMountNode] = useState(null);

  const user = useSelector((state) => state.auth.user);
  let token = user?.token || null;

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: ["timeOffData", id, token], // Unique key for caching
    queryFn: fetchTimeOffData,
    enabled: !!token, // Only run query if token exists
    onSuccess: (data) => {
      setTimeOffData(data);
    },
    onError: (err) => {
      setError(err.message || "Failed to fetch time off data");
      toast.error(err.message || "Error fetching time off data", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });

  // isRefetching is true when fetching but not initial loading
  const isRefetching = isFetching && !isLoading;

  useEffect(() => {
    dispatch(setHeaderText("Time Off"));
    window.scrollTo(0, 0);
  }, [dispatch]);

  useEffect(() => {
    if (isEditing && componentRef.current) {
      setEditMountNode(componentRef.current);
    } else if (!isEditing) {
      setEditMountNode(null);
    }
  }, [isEditing]);

  const downloadTimeOffDoc = async (timeoff) => {
    try {
      const response = await axios.get(`/api/time-off/download/${timeoff}`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      saveAs(pdfBlob, `${timeoff}`);
    } catch (error) {
      console.error(error);
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") {
        localStorage.removeItem("user");
        dispatch(logout());
        dispatch(reset());
        dispatch(invoiceReset());
        dispatch(headerReset());
        dispatch(appSettingsReset());
        toast.error("This user is deleted!", {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate("/");
        return;
      }
      toast.error("Failed to download document", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="single-ls-info timeoff-single-page">
      <div className="back-btn-ins">
        <Link to="/user/time-off/overview" className="btnf1-si-s">
          Back to All Time Offs
        </Link>
      </div>
      <div className="man-padd-for-siginvo">
        {isLoading ? (
          <>
            {/* TimeOffActions skeleton */}
            <div className="s1-contain s1-contain-with-comment">
              <div className="s1-contain-inner">
                <div className="bill-num-si">
                  {/* ID skeleton */}
                  <a className="invoice-num-01" style={{ display: "block", textDecoration: "none", pointerEvents: "none" }}>
                    <div
                      style={{
                        width: "255px",
                        height: "31.72px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "4px",
                        animation: "pulse 1.5s ease-in-out infinite",
                      }}
                    />
                  </a>
                  {/* Status badge skeleton */}
                  <div
                    style={{
                      width: "84px",
                      height: "31px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "100px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
                <div className="btn-aary">
                  {/* Edit button skeleton */}
                  <div
                    style={{
                      width: "100px",
                      height: "44px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "6px",
                      animation: "pulse 1.5s ease-in-out infinite",
                      marginRight: "14px",
                    }}
                  />
                  {/* Print button skeleton */}
                  <div
                    style={{
                      width: "92px",
                      height: "44px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "6px",
                      animation: "pulse 1.5s ease-in-out infinite",
                      marginRight: "14px",
                    }}
                  />
                  {/* Delete button skeleton */}
                  <div
                    style={{
                      width: "140px",
                      height: "44px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "6px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Main time off detail skeleton */}
            <div className="invoice-main-body">
              <div className="invoice-detail-bs1">
                {/* Header section skeleton */}
                <div className="main-head-bs11">
                  <div className="invoce-id-num">
                    <div className="invo-id">Type</div>
                    <div className="invo-num-1">
                      <div
                        style={{
                          width: "120px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </div>
                  </div>
                  <div className="invoce-id-type">
                    <div className="invo-id">User</div>
                    <div className="invo-num-1">
                      <div
                        style={{
                          width: "180px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </div>
                  </div>
                  <div className="invoce-id-exdate">
                    <div className="invo-id">Issue Date</div>
                    <div className="invo-num-1">
                      <div
                        style={{
                          width: "100px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Table skeleton */}
                <div className="table-outtter">
                  <div className="table-inner-outmain22">
                    <table className="invo-single-tb1" style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr className="mainborder-bottom">
                          <td style={{ width: "20%", paddingRight: "20px" }}>Start Date</td>
                          <td style={{ width: "20%", paddingRight: "20px" }}>End Date</td>
                          <td style={{ width: "20%", paddingRight: "20px" }}>Total Days</td>
                          <td style={{ width: "20%", paddingRight: "20px" }}>Start Time</td>
                          <td style={{ width: "20%", paddingRight: "20px" }}>End Time</td>
                        </tr>
                      </thead>
                      <tbody>
                        <tr valign="top" className="border-bottomiteam infotablem">
                          <td>
                            <div
                              style={{
                                width: "100px",
                                height: "20px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </td>
                          <td>
                            <div
                              style={{
                                width: "100px",
                                height: "20px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </td>
                          <td>
                            <div
                              style={{
                                width: "60px",
                                height: "20px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </td>
                          <td>
                            <div
                              style={{
                                width: "80px",
                                height: "20px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </td>
                          <td>
                            <div
                              style={{
                                width: "80px",
                                height: "20px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Remarks skeleton */}
                  <div className="other-comment" style={{ marginTop: "30px" }}>
                    <div className="other-head1">Remarks</div>
                    <div className="other-subhead1">
                      <div
                        style={{
                          width: "100%",
                          height: "60px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </div>
                  </div>

                  {/* Document skeleton */}
                  <div className="attachment-single-invi" style={{ marginTop: "30px" }}>
                    <div className="attachment-head">Document</div>
                    <div className="the-src-pdf-file">
                      <div className="bottom-download-outer">
                        <div className="bottom-download-inner">
                          <div className="bottom-download-item">
                            <div className="attachment-file-sib-pname">
                              <div
                                style={{
                                  width: "150px",
                                  height: "20px",
                                  backgroundColor: "#e5e7eb",
                                  borderRadius: "4px",
                                  animation: "pulse 1.5s ease-in-out infinite",
                                }}
                              />
                            </div>
                            <div className="attachment-file-sib-psize">
                              <div
                                style={{
                                  width: "50px",
                                  height: "20px",
                                  backgroundColor: "#e5e7eb",
                                  borderRadius: "4px",
                                  animation: "pulse 1.5s ease-in-out infinite",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="bottom-download-inner-right">
                          <div
                            style={{
                              width: "12px",
                              height: "15px",
                              backgroundColor: "#e5e7eb",
                              borderRadius: "2px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ManageTimeOff skeleton */}
              <div className="manage-detail-outer">
                <div className="manage-detail-bs2">
                  {/* Heading skeleton */}
                  <div className="invoice-manage-singlepg-head">
                    <div
                      style={{
                        width: "150px",
                        height: "26px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "4px",
                        animation: "pulse 1.5s ease-in-out infinite",
                      }}
                    />
                  </div>
                  <div className="single-invo-sattus">
                    {/* Request Status label skeleton */}
                    <div className="invo-main-status-head">
                      <div
                        style={{
                          width: "120px",
                          height: "18px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </div>
                    {/* Buttons skeleton */}
                    <div className="invo-sig-st-btns">
                      <div
                        className="Approve-singlebtn"
                        style={{
                          backgroundColor: "#e5e7eb",
                          borderRadius: "8px",
                          animation: "pulse 1.5s ease-in-out infinite",
                          cursor: "default",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            width: "80px",
                            height: "17px",
                            backgroundColor: "#cbd5e1",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </div>
                      <div
                        className="Reject-singlebtn"
                        style={{
                          backgroundColor: "#e5e7eb",
                          borderRadius: "8px",
                          animation: "pulse 1.5s ease-in-out infinite",
                          cursor: "default",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            width: "70px",
                            height: "17px",
                            backgroundColor: "#cbd5e1",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </div>
                      <div
                        className="cancel-singlebtn"
                        style={{
                          backgroundColor: "#e5e7eb",
                          borderRadius: "8px",
                          animation: "pulse 1.5s ease-in-out infinite",
                          cursor: "default",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            width: "70px",
                            height: "17px",
                            backgroundColor: "#cbd5e1",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pulse animation style */}
            <style>{`
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.5;
              }
            }
          `}</style>
          </>
        ) : (
          <>
            <div>
              <TimeOffActions
                timeOffData={timeOffData}
                refetch={refetch}
                componentRef={componentRef}
                isRefetching={isRefetching}
                isEditing={isEditing}
                onEditingChange={setIsEditing}
                editMountNode={editMountNode}
              />
            </div>
            <div className="invoice-main-body">
              <div
                className={`invoice-detail-bs1${isEditing ? " time-off-inline-edit-card" : ""}`}
                ref={componentRef}
              >
                {isEditing ? null : (
                <>
                <div className="main-head-bs11">
                  <div className="invoce-id-num">
                    <div className="invo-id">Type</div>
                    <div className="invo-num-1">
                      {isRefetching ? (
                        <div
                          style={{
                            width: "120px",
                            height: "20px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      ) : (
                        timeOffData?.type_of_leave.leave_type || "N/A"
                      )}
                    </div>
                  </div>
                  <div className="invoce-id-type">
                    <div className="invo-id">User</div>
                    <div className="invo-num-1">
                      {isRefetching ? (
                        <div
                          style={{
                            width: "180px",
                            height: "20px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      ) : (
                        timeOffData?.user && (
                          <>
                            {timeOffData.user.first_name
                              ? `${timeOffData.user.first_name} ${timeOffData.user.middle_name || ''} ${timeOffData.user.last_name || ''}`
                              : `${timeOffData.user.company_first_name
                                ? timeOffData.user.company_first_name + (timeOffData.user.company_last_name ? ` ${timeOffData.user.company_last_name}` : '')
                                : timeOffData.user.company_name || ''}`
                            }
                          </>
                        )
                      )}
                    </div>
                  </div>
                  <div className="invoce-id-exdate">
                    <div className="invo-id">Issue Date</div>
                    <div className="invo-num-1">
                      {isRefetching ? (
                        <div
                          style={{
                            width: "100px",
                            height: "20px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      ) : (
                        timeOffData
                          ? moment
                            .tz(timeOffData.created_at, timeOffData.timeZone || "Asia/Calcutta")
                            .locale("en-gb")
                            .format("DD/MM/YYYY")
                          : "Loading..."
                      )}
                    </div>
                  </div>
                </div>
                <div className="table-outtter">
                  <div className="table-inner-outmain22">
                    <table className="invo-single-tb1" style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr className="mainborder-bottom">
                          {isRefetching ? (
                            <>
                              <td style={{ width: "20%", paddingRight: "20px" }}>
                                <div
                                  style={{
                                    width: "80px",
                                    height: "18px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td style={{ width: "20%", paddingRight: "20px" }}>
                                <div
                                  style={{
                                    width: "70px",
                                    height: "18px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td style={{ width: "20%", paddingRight: "20px" }}>
                                <div
                                  style={{
                                    width: "85px",
                                    height: "18px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td style={{ width: "20%", paddingRight: "20px" }}>
                                <div
                                  style={{
                                    width: "80px",
                                    height: "18px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td style={{ width: "20%", paddingRight: "20px" }}>
                                <div
                                  style={{
                                    width: "70px",
                                    height: "18px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              {timeOffData?.start_date && (
                                <td style={{ width: "20%", paddingRight: "20px" }}>Start Date</td>
                              )}
                              {timeOffData?.end_date && (
                                <td style={{ width: "20%", paddingRight: "20px" }}>End Date</td>
                              )}
                              {timeOffData?.start_date && timeOffData?.end_date && (
                                <td style={{ width: "20%", paddingRight: "20px" }}>Total Days</td>
                              )}
                              {timeOffData?.start_time !== undefined && timeOffData?.start_time !== null && (
                                <td style={{ width: "20%", paddingRight: "20px" }}>Start Time</td>
                              )}
                              {timeOffData?.end_time !== undefined && timeOffData?.end_time !== null && (
                                <td style={{ width: "20%", paddingRight: "20px" }}>End Time</td>
                              )}
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr valign="top" className="border-bottomiteam infotablem">
                          {isRefetching ? (
                            <>
                              <td>
                                <div
                                  style={{
                                    width: "100px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td>
                                <div
                                  style={{
                                    width: "100px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td>
                                <div
                                  style={{
                                    width: "60px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td>
                                <div
                                  style={{
                                    width: "80px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                              <td>
                                <div
                                  style={{
                                    width: "80px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              {timeOffData?.start_date && (
                                <td>
                                  {moment
                                    .tz(timeOffData.start_date, timeOffData.timeZone || "Asia/Calcutta")
                                    .locale("en-gb")
                                    .format("DD/MM/YYYY")}
                                </td>
                              )}
                              {timeOffData?.end_date && (
                                <td>
                                  {moment
                                    .tz(timeOffData.end_date, timeOffData.timeZone || "Asia/Calcutta")
                                    .locale("en-gb")
                                    .format("DD/MM/YYYY")}
                                </td>
                              )}
                              {timeOffData?.start_date && timeOffData?.end_date && (
                                <td>
                                  {(() => {
                                    const start = moment.tz(timeOffData.start_date, timeOffData.timeZone || "Asia/Calcutta").startOf("day");
                                    const end = moment.tz(timeOffData.end_date, timeOffData.timeZone || "Asia/Calcutta").startOf("day");
                                    const dayDiff = end.diff(start, "days");

                                    if (dayDiff > 0) {
                                      return dayDiff + 1;
                                    } else {
                                      const startTime = timeOffData.start_time || 0;
                                      const endTime = timeOffData.end_time || 0;
                                      const totalHours = endTime - startTime;
                                      if (totalHours >= 8) return 1;
                                      if (totalHours > 0) return (totalHours / 8).toFixed(1);
                                      return 0;
                                    }
                                  })()}
                                </td>
                              )}
                              {timeOffData?.start_time !== undefined && timeOffData?.start_time !== null && (
                                <td>
                                  {moment()
                                    .startOf("day")
                                    .add(timeOffData.start_time, "hours")
                                    .format("hh:mm A")}
                                </td>
                              )}
                              {timeOffData?.end_time !== undefined && timeOffData?.end_time !== null && (
                                <td>
                                  {moment()
                                    .startOf("day")
                                    .add(timeOffData.end_time, "hours")
                                    .format("hh:mm A")}
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {isRefetching ? (
                    <div className="other-comment" style={{ marginTop: "30px" }}>
                      <div className="other-head1">Remarks</div>
                      <div className="other-subhead1">
                        <div
                          style={{
                            width: "100%",
                            height: "60px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    timeOffData && timeOffData.note && (
                      <div className="other-comment">
                        <div className="other-head1">Remarks</div>
                        <div className="other-subhead1">{timeOffData.note}</div>
                      </div>
                    )
                  )}

                  {isRefetching && timeOffData?.timeoff_document ? (
                    <div className="attachment-single-invi" style={{ marginTop: "30px" }}>
                      <div className="attachment-head">Document</div>
                      <div className="the-src-pdf-file">
                        <div className="bottom-download-outer">
                          <div className="bottom-download-inner">
                            <div className="bottom-download-item">
                              <div className="attachment-file-sib-pname">
                                <div
                                  style={{
                                    width: "150px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </div>
                              <div className="attachment-file-sib-psize">
                                <div
                                  style={{
                                    width: "50px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="bottom-download-inner-right">
                            <div
                              style={{
                                width: "12px",
                                height: "15px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "2px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    timeOffData?.timeoff_document && (
                      <div className="attachment-single-invi">
                        <div className="attachment-head">Document</div>
                        <div className="the-src-pdf-file" style={{ cursor: "pointer" }}>
                          <div className="bottom-download-outer">
                            <div className="bottom-download-inner">
                              <div className="bottom-download-item">
                                <div className="attachment-file-sib-pname">
                                  {timeOffData.timeoff_document.length > 15
                                    ? <span className="shortened-name" title={timeOffData.timeoff_document}>{timeOffData.timeoff_document.slice(0, 15)}...</span>
                                    : timeOffData.timeoff_document || "N/A"}
                                </div>
                                <div className="attachment-file-sib-psize">
                                  {getFileExtension(timeOffData.timeoff_document)?.toUpperCase()}
                                </div>
                              </div>
                            </div>
                            <div className="bottom-download-inner-right" onClick={() => downloadTimeOffDoc(timeOffData.timeoff_document)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="15" viewBox="0 0 12 15" fill="none">
                                <path d="M5.67409 13.8727C5.69551 13.8928 5.71891 13.9108 5.74395 13.9266L5.77888 13.9439L5.82304 13.9665L5.86918 13.9804L5.90971 13.9909C5.97034 14.003 6.03328 14.003 6.09425 13.9909L6.13478 13.9804L6.18091 13.9665L6.22507 13.9439L6.26 13.9266C6.28537 13.9108 6.30877 13.8928 6.33019 13.8727L10.8939 9.57954C11.0458 9.40745 11.0333 9.1549 10.8649 8.99674C10.6965 8.83858 10.4276 8.82682 10.2444 8.9695L6.46134 12.526V1.43331C6.46134 1.19406 6.25473 1 6 1C5.74527 1 5.53865 1.19406 5.53865 1.43331V12.526L1.75563 8.9695C1.57241 8.82682 1.30351 8.83858 1.13512 8.99674C0.966727 9.1549 0.954205 9.40745 1.10612 9.57954L5.67343 13.8727H5.67409Z" fill="#282828" stroke="#282828" strokeWidth="0.5" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
                </>
              )}
              </div>
              {!isEditing && (
                <ManageTimeOff
                  user={user}
                  timeOffData={timeOffData}
                  setTimeOffData={setTimeOffData}
                  token={token}
                  isRefetching={isRefetching}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Utility function to get file extension
const getFileExtension = (filename) => {
  return filename ? filename.split(".").pop() : null;
};

export default Single;